import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ExtendedWebsiteMessage, MessageAction, MessageActionButton } from '../../types/messages';
import { MessageDisplayHelpers } from '../../types/messages';
import { supabase } from '../../services/supabaseClient';
import ContactMessageReply from './ContactMessageReply';
import './MessageDetailPanel.css';

interface MessageDetailPanelProps {
  message: ExtendedWebsiteMessage | null;
  onAction: (action: MessageAction) => void;
  onClose?: () => void;
  onLinkClick?: (url: string, isExternal: boolean) => void;
}

/**
 * Email-style message detail panel
 * Shows full message content when a message is selected
 */
const MessageDetailPanel: React.FC<MessageDetailPanelProps> = ({
  message,
  onAction,
  onClose,
  onLinkClick
}) => {
  const { t } = useLanguage();
  const isUnread = message ? !message.viewedAt : false;
  const [contactMessageId, setContactMessageId] = useState<string | null>(null);

  // Fetch contact_message_id if this is a contact_form message
  useEffect(() => {
    const fetchContactMessageId = async () => {
      if (message && message.message_type === 'contact_form') {
        try {
          const { data, error } = await supabase
            .from('contact_messages')
            .select('id')
            .eq('message_id', message.id)
            .single();

          if (!error && data) {
            setContactMessageId(data.id);
          }
        } catch (err) {
          console.error('Error fetching contact message ID:', err);
        }
      } else {
        setContactMessageId(null);
      }
    };

    fetchContactMessageId();
  }, [message?.id, message?.message_type]);

  // Get message displays
  const typeDisplay = useMemo(() =>
    message ? MessageDisplayHelpers.getMessageTypeDisplay(message.message_type) : { icon: '', label: '', className: '' },
    [message?.message_type]
  );

  const sourceDisplay = useMemo(() => {
    if (!message) return { label: '', className: '', fullLabel: '', icon: '' };
    const display = MessageDisplayHelpers.getMessageSourceDisplay(
      message.messageSource,
      message.projectTitle,
      message.taskTitle,
      message.questionnaireTitle
    );
    const iconMap: Record<string, string> = {
      'global': '🌐',
      'project_release': '🚀',
      'task_related': '✓',
      'survey_related': '📊'
    };
    return {
      ...display,
      icon: iconMap[message.messageSource] || '📬'
    };
  }, [message?.messageSource, message?.projectTitle, message?.taskTitle, message?.questionnaireTitle]);

  // Parse action buttons
  const actionButtons = useMemo(() =>
    message ? MessageDisplayHelpers.parseActionButtons(message.message_text) : [],
    [message?.message_text]
  );

  // Clean content without action button markup
  const cleanContent = useMemo(() => {
    if (!message) return '';
    let content = MessageDisplayHelpers.removeActionButtonMarkup(message.message_text);

    // Process markdown-style links for task and team messages
    if (['task_pending_review', 'task_approved', 'task_rejected'].includes(message.message_type)) {
      content = MessageDisplayHelpers.processTaskMessageLinks(content, message.message_type);
    } else if (['team_application_received', 'team_application_approved', 'team_application_rejected', 'team_formed'].includes(message.message_type)) {
      content = MessageDisplayHelpers.processTeamMessageLinks(content);
    }

    return content;
  }, [message?.message_text, message?.message_type]);

  // Handle link clicks
  const handleLinkClick = useCallback((url: string, isExternal: boolean) => {
    if (onLinkClick) {
      onLinkClick(url, isExternal);
    } else {
      // Default behavior
      if (isExternal) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  }, [onLinkClick]);

  // Helper function to convert markdown to HTML
  const convertMarkdownToHtml = useCallback((text: string): string => {
    let html = text;
    // Convert **bold** to <strong>
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Convert *italic* to <em>
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Convert [link](url) to <a>
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    return html;
  }, []);

  // Make links clickable
  const processedContent = useMemo(() => {
    if (!message) return '';

    // Contact messages, task messages, and team messages already contain formatted HTML
    if (['task_pending_review', 'task_approved', 'task_rejected', 'team_application_received', 'team_application_approved', 'team_application_rejected', 'team_formed', 'contact_form', 'contact_reply'].includes(message.message_type)) {
      // Check if content actually has HTML tags
      if (/<[^>]*>/g.test(cleanContent)) {
        return cleanContent; // Already contains HTML
      } else {
        // Fallback: content is plain text, format it nicely
        console.warn('[MessageDetailPanel] Expected HTML but got plain text for', message.message_type);

        // For contact_reply, try to parse and format the plain text
        if (message.message_type === 'contact_reply') {
          const lines = cleanContent.split('\n').filter(line => line.trim());
          let formattedHtml = '<div class="contact-reply-fallback" style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif; line-height: 1.6;">';

          lines.forEach((line, index) => {
            const trimmed = line.trim();
            // Check if it's a header line (contains emoji or special markers)
            if (trimmed.includes('💬') || trimmed.includes('Reply to Your Contact Form')) {
              formattedHtml += `<div style="background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%); padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #93C5FD;"><h3 style="margin: 0; color: #1E40AF; font-size: 18px; font-weight: 700;">${convertMarkdownToHtml(trimmed)}</h3></div>`;
            } else if (trimmed.startsWith('Re:') || trimmed.includes('**Re:**')) {
              const subject = trimmed.replace(/\*\*Re:\*\*/g, '').replace(/\*\*/g, '').replace('Re:', '').trim();
              formattedHtml += `<div style="background: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 16px; margin-bottom: 16px;"><div style="color: #6B7280; font-size: 11px; text-transform: uppercase; margin-bottom: 6px;">Subject</div><div style="color: #111827; font-size: 16px; font-weight: 600;">Re: ${subject}</div></div>`;
            } else if (trimmed.includes('Reply from') || trimmed.includes('Support Team')) {
              formattedHtml += `<div style="background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%); padding: 12px; border-radius: 8px; margin-top: 16px; text-align: center; border: 1px solid #BAE6FD;"><p style="margin: 0; color: #0C4A6E; font-size: 13px; font-weight: 600;">${convertMarkdownToHtml(trimmed)}</p></div>`;
            } else if (trimmed.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
              // Date/time line
              formattedHtml += `<div style="color: #6B7280; font-size: 13px; margin-bottom: 16px;"><strong>${t('messagesPage.sent')}</strong> ${trimmed}</div>`;
            } else if (trimmed.length > 0) {
              // Regular content - convert markdown
              formattedHtml += `<div style="background: #FFFFFF; border: 2px solid #E5E7EB; border-radius: 12px; padding: 20px; margin-bottom: 16px;"><div style="color: #1F2937; font-size: 15px; line-height: 1.8; white-space: pre-wrap;">${convertMarkdownToHtml(trimmed)}</div></div>`;
            }
          });

          formattedHtml += '</div>';
          return formattedHtml;
        }

        // For contact_form and other types, convert markdown and wrap in a div
        const htmlContent = convertMarkdownToHtml(cleanContent);
        return `<div style="white-space: pre-wrap; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; padding: 16px; background: #F9FAFB; border-radius: 8px;">${htmlContent}</div>`;
      }
    }
    return MessageDisplayHelpers.makeLinksClickable(cleanContent, handleLinkClick);
  }, [cleanContent, handleLinkClick, message?.message_type, convertMarkdownToHtml]);

  // Handle actions
  const handleBookmark = useCallback(() => {
    if (!message) return;
    onAction(message.isBookmarked ? 'unbookmark' : 'bookmark');
  }, [message, onAction]);

  const handleMarkAsRead = useCallback(() => {
    onAction(isUnread ? 'mark_read' : 'mark_unread');
  }, [isUnread, onAction]);

  const handleActionButton = useCallback((button: MessageActionButton) => {
    if (button.url) {
      const isExternal = button.url.startsWith('http');
      handleLinkClick(button.url, isExternal);
    }
    onAction('action_button_click');
  }, [handleLinkClick, onAction]);

  // If no message selected, show empty state
  if (!message) {
    return (
      <div className="message-detail-panel empty">
        <div className="empty-state">
          <div className="empty-icon">📬</div>
          <h3>{t('messagesPage.noSelection')}</h3>
          <p>{t('messagesPage.noSelectionHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-detail-panel">
      {/* Header */}
      <div className="message-detail-header">
        <div className="message-detail-header-left">
          <div className="message-detail-type">
            <span className="type-icon">{typeDisplay.icon}</span>
            <h2>{typeDisplay.label}</h2>
          </div>
          <div className="message-detail-source">
            <span className={`source-badge source-${message.messageSource}`}>
              {sourceDisplay.icon} {sourceDisplay.fullLabel}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="message-detail-actions">
          <button
            className={`action-btn ${message.isBookmarked ? 'active' : ''}`}
            onClick={handleBookmark}
            title={message.isBookmarked ? t('messagesPage.unbookmark') : t('messagesPage.bookmark')}
            aria-label={message.isBookmarked ? t('messagesPage.unbookmark') : t('messagesPage.bookmark')}
          >
            {message.isBookmarked ? '⭐' : '☆'}
          </button>
          <button
            className={`action-btn ${!isUnread ? 'active' : ''}`}
            onClick={handleMarkAsRead}
            title={isUnread ? t('messagesPage.markRead') : t('messagesPage.markUnread')}
            aria-label={isUnread ? t('messagesPage.markRead') : t('messagesPage.markUnread')}
          >
            {isUnread ? '📧' : '📭'}
          </button>
          {onClose && (
            <button
              className="action-btn close-btn"
              onClick={onClose}
              title={t('messagesPage.close')}
              aria-label={t('messagesPage.closeDetail')}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="message-detail-meta">
        <div className="message-detail-time">
          <span className="meta-icon">🕐</span>
          <span>{message.displayTimestamp}</span>
          <span className="time-ago">({message.timeAgo})</span>
        </div>
        {message.viewedAt && (
          <div className="message-detail-read-time">
            <span className="meta-icon">👁</span>
            <span>{t('messagesPage.readAt')} {MessageDisplayHelpers.getTimeAgo(message.viewedAt)}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="message-detail-content">
        <div
          className="message-content-text"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      </div>

      {/* Action Buttons */}
      {actionButtons.length > 0 && (
        <div className="message-detail-buttons">
          {actionButtons.map((button) => (
            <button
              key={button.id}
              className={`message-action-button ${button.style || 'primary'}`}
              onClick={() => handleActionButton(button)}
              disabled={button.disabled}
            >
              {button.text}
            </button>
          ))}
        </div>
      )}

      {/* Contact Message Reply - Only shown for contact_form messages */}
      {message.message_type === 'contact_form' && contactMessageId && (
        <ContactMessageReply
          contactMessageId={contactMessageId}
          onReplySuccess={() => {
            // Optionally refresh messages or show confirmation
            console.log('Reply sent successfully');
          }}
        />
      )}
    </div>
  );
};

export default React.memo(MessageDetailPanel);
